import PropTypes from 'prop-types';
import ErrorMessage from './ErrorMessage.json';
import { getBulkItems, removeItem, setBulkItems } from '../lib/cookies';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../hooks/useAuth';
import { NON_SECURE_PATH } from '../config/commonConfig';
import moment from 'moment';
import { axiosGetCall } from 'src/services/apisCall';

//For handle apis error response message.
const prepareMessageFromError = (error) => {
  return error?.response?.data?.message || error?.message || ErrorMessage?.[4000];
};

//Define prop type of fn.
prepareMessageFromError.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // pass complete error or custom message.
};

// Set user session.
const setLoginSession = (exp, sessionToken, token) => {
  return setBulkItems([
    { key: 'exp', value: exp },
    { key: 'sessionToken', value: sessionToken },
    { key: 'token', value: token },
  ]);
};

const removeLoginSession = () => {
  removeItem('exp');
  removeItem('sessionToken');
  localStorage.removeItem('token');
};

setLoginSession.propTypes = {
  exp: PropTypes.number.isRequired,
  sessionToken: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
};

// Get user session.
const getLoginSession = () => {
  const cookiesNameArray = ['exp', 'sessionToken', 'token'];
  return getBulkItems(cookiesNameArray);
};

// convert date to ts
const dateToTs = (date) => {
  const dateArray = date.split('-');
  const tsDate = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
  return tsDate.getTime();
};

const checkRouteAccess = (path) => {
  try {
    const auth = useAuth();
    if (!auth) {
      removeLoginSession();
      return false;
    }

    const { access } = jwtDecode(localStorage.getItem('token'));
    if (NON_SECURE_PATH.includes(path)) return true;

    // Build key → access entry map from the JWT (mirrors checkButtonAccess).
    const keyToAccess = Object.values(access).reduce((acc, entry) => {
      if (entry.key) acc[entry.key] = entry;
      return acc;
    }, {});

    const accessPathMapper = {
      '/kyc': keyToAccess?.kycPageList?.r,
      '/console': keyToAccess?.consolePageList?.r || keyToAccess?.advertisementPageList?.r,
      '/console_users': keyToAccess?.consolePageList?.r,
      '/plans': keyToAccess?.createConsoleuser?.r, //Todo update it
      '/users': keyToAccess?.adminPageList?.r && checkUserRole() !== 'agent',
      '/add_new_user': keyToAccess?.addNewUser?.w,
      '/advertisement': keyToAccess?.advertisementPageList?.r,
      '/meet': keyToAccess?.meetPageList?.r,
      '/webinar': keyToAccess?.webinarPageList?.r,
      '/sms': keyToAccess?.smsGatewayPageList?.r,
      '/home': keyToAccess?.homePageList?.r,
      '/leads': keyToAccess?.leads?.r,
      '/updatepermissions': checkUserRole() === 'superAdmin',
      '/createUser': keyToAccess?.createConsoleuser?.r,
      '/announcementList': keyToAccess?.viewAnnouncementIvrList?.r,
      '/voice': keyToAccess?.viewAnnouncementIvrList?.r,
      '/ivrList': keyToAccess?.viewAnnouncementIvrList?.r,
      '/ttsTemplateList': keyToAccess?.viewAnnouncementIvrList?.r,
      '/billing': keyToAccess?.billingPageList?.r,
      '/branding': keyToAccess?.advertisementPageList?.r,
      '/amAnlysis': keyToAccess?.advertisementPageList?.r,
      '/whatsapp_plans': keyToAccess?.addWhatsApp?.r,
      '/email': keyToAccess?.emailPageList?.r,
      '/voice_plans': keyToAccess?.viewAnnouncementIvrList?.r,
      '/server': keyToAccess?.serverPageList?.r,
      '/emailStatus': keyToAccess?.emailPageList?.r,
      '/voice-deleted-userList': keyToAccess?.voicedeleteuserlist?.r,
      '/smsTemplateList': keyToAccess?.smsTemplateList?.r,
      '/senderIdList': keyToAccess?.senderIdList?.r,
      '/smsPlans': keyToAccess?.smsPlans?.r,
      '/console_auth': keyToAccess?.authPageList?.r,
      '/voice-credit-amount-invoice': keyToAccess?.amountcreditinvoice?.r,
      '/voice_deleted_campaign_list': keyToAccess?.voicedeleteuserlist?.r,
      '/elastic_backup_server': keyToAccess?.elasticBackupServer?.r,
      '/NegativeVoiceBalance': keyToAccess?.viewUserVoicePlan?.r,
      '/voiceHost': keyToAccess?.host?.r,
      '/voiceGateway': keyToAccess?.voiceGateway?.r,
      '/voiceCli': keyToAccess?.voiceCli?.r,
      '/voiceAdminSetting': keyToAccess?.voiceAdminSetting?.r,
      '/voiceAdminSettingList': keyToAccess?.voiceAdminSetting?.r,
      '/smsAdminSetting': keyToAccess?.smsAdminSetting?.r,
      '/smsAdminSettingList': keyToAccess?.smsAdminSetting?.r,
      '/smsRealNumber': keyToAccess?.smsRealNumber?.r,
      '/retrySettings': keyToAccess?.retrySettings?.r,
      '/implicitValidityPeriod': keyToAccess?.implicitValidityPeriod?.r,
    };
    if (Object.keys(accessPathMapper).includes(path)) {
      return accessPathMapper[path];
    }
    return false;
  } catch (error) {
    console.log(error);
  }
};

const checkButtonAccess = (key, accessType) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const { access } = jwtDecode(token);
    const keyToAccess = Object.values(access).reduce((acc, entry) => {
      if (entry.key) acc[entry.key] = entry;
      return acc;
    }, {});
    return keyToAccess[key]?.[accessType] ?? false;
  } catch {
    return false;
  }
};

const checkUserRole = () => {
  const auth = useAuth();
  if (!auth) return auth;
  const { role } = jwtDecode(localStorage.getItem('token'));
  return role;
};

const copyToClipBoard = (text) => {
  navigator.clipboard.writeText(text);
};

// formate name like first letter upper case.
const nameFormater = (value) => {
  return value && value[0].toUpperCase() + value.slice(1).toLowerCase();
};

const compareObjectValues = (prev, updated) => {
  for (let [key] of Object.entries(updated)) {
    if (updated?.[key] && prev[key] !== updated[key]) {
      return true;
    }
  }
  return false;
};

// For random password purpose
function generateRandomString() {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const specialChars = '!@#$%^&*()_+[]{}|?';

  const allChars = lowercase + uppercase + digits + specialChars;
  const length = Math.floor(Math.random() * 13) + 8; // Length between 8 and 20

  let result = '';

  // Ensure the result has at least one of each required character
  result += lowercase[Math.floor(Math.random() * lowercase.length)];
  result += uppercase[Math.floor(Math.random() * uppercase.length)];
  result += digits[Math.floor(Math.random() * digits.length)];
  result += specialChars[Math.floor(Math.random() * specialChars.length)];

  // Fill the rest of the string with random characters
  for (let i = result.length; i < length; i++) {
    result += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the result to ensure randomness
  result = result
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('');

  return result;
}

const extractPlainText = (htmlContent) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  return doc.body.textContent || '';
};

const getToken = async () => {
  const token = await localStorage.getItem('token');
  return token;
};

/**
 * Formats a given date into "MMM DD, ddd" format (e.g., "Dec 25, Wed").
 * @param {string | Date | moment.Moment} date - The date to format.
 * @returns {string} - The formatted date string.
 */
function formatDateMDW(date) {
  if (!date) {
    throw new Error('A valid date is required');
  }
  return moment.utc(date).format('MMM DD, ddd'); // Dec 28 Sunday
}

/**
 * Calculates the difference between two times in hours and minutes.
 * @param {string | Date | moment.Moment} startTime - The start time of the shift.
 * @param {string | Date | moment.Moment} endTime - The end time of the shift.
 * @returns {string} - The difference in the format "X hours Y minutes".
 */
function calculateShiftDuration(startTime, endTime) {
  if (!startTime || !endTime) {
    throw new Error('Both startTime and endTime are required');
  }

  const start = moment(startTime);
  const end = moment(endTime);

  if (!start.isValid() || !end.isValid()) {
    throw new Error('Invalid startTime or endTime');
  }

  const duration = moment.duration(end.diff(start));
  const hours = duration.hours();
  const minutes = duration.minutes();

  return `${hours} h ${minutes} m`;
}

const getSupportColumns = async () => {
  try {
    const { result } = await axiosGetCall('getColumnDetails');
    return result;
  } catch (error) {
    throw new Error(error);
  }
};

function formatDate(dateString) {
  // Create a Date object from the input string
  const date = new Date(dateString);

  const offsetIST = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
  const dateIST = new Date(date.getTime() + offsetIST);

  // Extract hours and minutes
  let hours = dateIST.getUTCHours();
  const minutes = dateIST.getUTCMinutes();

  // Determine AM or PM
  const ampm = hours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // If hour is 0, set it to 12

  // Format minutes to always be two digits
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  // // Extract date (in IST)
  // const year = dateIST.getUTCFullYear();
  // const month = dateIST.getUTCMonth() + 1; // Months are 0-indexed
  // const day = dateIST.getUTCDate();

  // Return formatted date
  return `${hours}:${formattedMinutes} ${ampm}`;
}

const dateFormate = (value, _formate) => {
  const date = moment(value).format("D MMMM YYYY");
  const time = moment(value).format("h:mm a");
  return { date, time };
};

export {
  prepareMessageFromError,
  setLoginSession,
  getLoginSession,
  dateToTs,
  checkRouteAccess,
  checkButtonAccess,
  checkUserRole,
  removeLoginSession,
  copyToClipBoard,
  nameFormater,
  compareObjectValues,
  generateRandomString,
  extractPlainText,
  getToken,
  formatDateMDW,
  calculateShiftDuration,
  getSupportColumns,
  formatDate,
  dateFormate,
};
